import React from 'react';
import { createPortal } from 'react-dom';

import type { HudState, RectSnapshot, TouchEventLogPayload } from './types';

export type TouchDiagnosticsOverlayProps = {
  overlay: boolean;
  lastEvent: TouchEventLogPayload | null;
  barRect: RectSnapshot | null;
  hudState: HudState | null;
};

/**
 * Visual debug layer rendered only when `overlay: true` in the LD flag config.
 * Draws markers in two distinct layers:
 * 1. Fixed layer (in client coordinates)
 * 2. Absolute layer (in page coordinates, appended to document.body)
 *
 * If the fixed-position bar's hit-test or rendering region diverges from layout,
 * the markers and rects in the two layers visually separate on screen recordings.
 */
export const TouchDiagnosticsOverlay = ({
  overlay,
  lastEvent,
  barRect,
  hudState,
}: TouchDiagnosticsOverlayProps): JSX.Element | null => {
  if (!overlay || typeof document === 'undefined') {
    return null;
  }

  const { scrollX, scrollY } = window;

  const content = (
    <div data-testid='kiosk-pro-touch-diagnostics-overlay' aria-hidden='true' style={{ pointerEvents: 'none' }}>
      {/* Absolute layer: page coordinates */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 2147483646,
          overflow: 'visible',
        }}
      >
        {barRect && (
          <div
            style={{
              position: 'absolute',
              left: barRect.left + scrollX,
              top: barRect.top + scrollY,
              width: barRect.width,
              height: barRect.height,
              border: '2px dashed #0088ff',
              boxSizing: 'border-box',
              color: 'var(--dcm-touch-diagnostics-abs-color, #0088ff)',
              fontSize: 10,
              fontFamily: 'var(--dcm-touch-diagnostics-font-family, monospace)',
              padding: 2,
            }}
          >
            ABS BAR
          </div>
        )}

        {lastEvent && (
          <div
            style={{
              position: 'absolute',
              left: lastEvent.coords.pageX - 16,
              top: lastEvent.coords.pageY - 16,
              width: 32,
              height: 32,
              borderRadius: 'var(--dcm-touch-diagnostics-marker-radius, 50%)',
              border: '3px solid #0088ff',
              backgroundColor: 'rgba(0, 136, 255, 0.25)',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>

      {/* Fixed layer: client coordinates */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 2147483647,
          overflow: 'hidden',
        }}
      >
        {barRect && (
          <div
            style={{
              position: 'fixed',
              left: barRect.left,
              top: barRect.top,
              width: barRect.width,
              height: barRect.height,
              border: '2px dashed #00ff00',
              boxSizing: 'border-box',
              color: 'var(--dcm-touch-diagnostics-fixed-color, #00ff00)',
              fontSize: 10,
              fontFamily: 'var(--dcm-touch-diagnostics-font-family, monospace)',
              padding: 2,
            }}
          >
            FIXED BAR
          </div>
        )}

        {lastEvent && (
          <div
            style={{
              position: 'fixed',
              left: lastEvent.coords.clientX - 10,
              top: lastEvent.coords.clientY - 10,
              width: 20,
              height: 20,
              borderRadius: 'var(--dcm-touch-diagnostics-marker-radius, 50%)',
              backgroundColor: 'rgba(255, 0, 0, 0.75)',
              border: '2px solid #ffffff',
              boxSizing: 'border-box',
            }}
          />
        )}

        {/* Small HUD panel */}
        <div
          data-testid='kiosk-pro-touch-diagnostics-hud'
          style={{
            position: 'fixed',
            top: 10,
            right: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: 'var(--dcm-touch-diagnostics-hud-color, #00ff66)',
            fontFamily: 'var(--dcm-touch-diagnostics-font-family, monospace)',
            fontSize: 11,
            lineHeight: 1.3,
            padding: '6px 10px',
            borderRadius: 'var(--dcm-touch-diagnostics-hud-radius, 6px)',
            maxWidth: 320,
            pointerEvents: 'none',
            border: '1px solid #00aa44',
          }}
        >
          <div>DIAG: ACTIVE [SANDBOX]</div>
          {hudState ? (
            <>
              <div>{`${hudState.eventType}: c(${hudState.clientX}, ${hudState.clientY}) p(${hudState.pageX}, ${hudState.pageY})`}</div>
              <div>{`InsideBar: ${String(hudState.insideBar)} | Hit: ${String(hudState.hitBar)}`}</div>
              <div>{`ProbeOffY: ${hudState.probeOffsetY !== null ? String(hudState.probeOffsetY) : 'n/a'}`}</div>
            </>
          ) : (
            <div>Awaiting touch input...</div>
          )}
        </div>
      </div>
    </div>
  );

  // Both absolute and fixed layers are portaled directly into document.body so they share the initial containing block and the only variable between them is position: fixed vs position: absolute.
  return createPortal(content, document.body);
};
